import { useEffect, useRef, useState } from 'react'

import {
    NotificationContainer,
    NotificationContainerProps,
} from '../../../../components/notification/notification-container.js'
import { PageHeader, PageHeaderProps } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import {
    SignatureCreation,
    SignatureCreationProps,
} from '../../../../modules/signature-creation/signature-creation.js'
import { getNotificationProps } from '../../../../props/notifications.js'
import { View } from '../../../../types.js'
import { loadSignaturesIfNeeded } from '../../../../utils/load-utils.js'
import {
    SignatureFormData,
    signatureItemToFormData,
} from '../../../../utils/signature-form-utils.js'

export interface SignatureCreationViewProps {
    hero: PageHeaderProps
    signatureCreation: SignatureCreationProps
    view: View
    mode: 'create' | 'edit'
    signatureId?: string
    company?: 'play' | 'nope'
}

export const SignatureCreationView = ({
    hero,
    signatureCreation: baseSignatureCreation,
    view,
    mode,
    signatureId,
    company,
}: SignatureCreationViewProps) => {
    const [initialData, setInitialData] = useState<SignatureFormData | undefined>(
        baseSignatureCreation.initialData,
    )
    const processedSignatureIdRef = useRef<string | null>(null)

    useEffect(() => {
        if (mode === 'edit' && signatureId) {
            loadSignaturesIfNeeded(view)
        }
    }, [mode, signatureId, view])

    useEffect(() => {
        if (mode !== 'edit' || !signatureId) {
            return
        }

        if (processedSignatureIdRef.current === signatureId) {
            return
        }

        const signatures = view.state.signatures.data
        if (!signatures?.data) {
            return
        }

        const signature = signatures.data.find((sig) => sig.id.toString() === signatureId)
        if (signature) {
            const formData = signatureItemToFormData(signature)
            setInitialData(formData)
            processedSignatureIdRef.current = signatureId
        }
    }, [mode, signatureId, view.state.signatures.data])

    useEffect(() => {
        if (mode === 'create' && company) {
            setInitialData({
                name: '',
                position: '',
                email: '',
                tags: company === 'nope' ? ['nope'] : ['play'],
                rows: [],
                imageUrl: undefined,
                company,
            })
            processedSignatureIdRef.current = null
        }
    }, [mode, company])

    const handleSave = async (formData: SignatureFormData) => {
        const result = baseSignatureCreation.onSave?.(formData)
        if (result instanceof Promise) {
            await result
        }
    }

    const signatureCreation: SignatureCreationProps = {
        ...baseSignatureCreation,
        initialData,
        onSave: handleSave,
        signatureId,
        company,
    }

    const notificationProps: NotificationContainerProps = getNotificationProps(view)
    const hasNotifications = notificationProps.notifications.length > 0

    return (
        <>
            <PageShell>
                <PageHeader {...hero} />
                <SignatureCreation {...signatureCreation} />
            </PageShell>
            {hasNotifications && <NotificationContainer {...notificationProps} />}
        </>
    )
}
